// Értesítés-feed („harang") feature — query-kulcs gyár + a feed-lap típusa. A tényleges hívásokat a
// hookok/komponensek a tipizált ts-rest kliensen (`api.notifications*`) intézik; a kulcsok itt élnek
// (cache-egységesítés + invalidálás). A `@atlas/contracts` a szerződés forrás-igazsága — itt csak
// kulcs-/típus-logika van (a többi feature mintája: features/payment-cards/api.ts).
import { type NotificationRowSchema } from '@atlas/contracts';
import { type InfiniteData } from '@tanstack/react-query';
import { type z } from 'zod';

import { type api } from '@/lib/api';

/** Egy értesítés-sor (a BFF `/notifications` feedjéből). A SZÖVEG nincs benne — azt a web rendereli. */
export type NotificationRow = z.infer<typeof NotificationRowSchema>;

/** Egy feed-LAP a ts-rest kliensből (siker és hiba egyaránt ide resolve-ol: `{ status, body }`). */
export type NotificationFeedPage = Awaited<ReturnType<typeof api.notifications.query>>;

/** A teljes (lapozott) feed-cache alakja — a `useInfiniteQuery` tárolt adata. */
export type NotificationFeed = InfiniteData<NotificationFeedPage>;

/** A panel egy lapja — kicsi, mert a harang alatt jelenik meg („továbbiak" gombbal lapozunk). */
export const NOTIFICATION_PAGE_SIZE = 20;

/**
 * Query-kulcs gyár. A `feeds()` a KÖZÖS prefix: erre invalidálunk (SSE-jelzés, mutáció-`onSettled`) és
 * ezen a prefixen írunk optimista frissítést (`setQueriesData`), így minden nyitott feed-variáns (pl.
 * későbbi „csak olvasatlan" nézet) EGYSZERRE frissül.
 */
export const notificationKeys = {
  all: ['notifications'] as const,
  feeds: () => ['notifications', 'feed'] as const,
  feed: (filters: { unreadOnly: boolean }) => ['notifications', 'feed', filters] as const,
};

/** Az olvasatlan-szám a legfrissebb (első) lapból; hibás/üres feed → 0. */
export function unreadCountOf(feed: NotificationFeed | undefined): number {
  const first = feed?.pages[0];
  return first?.status === 200 ? first.body.unreadCount : 0;
}

/** A betöltött lapok sorai egy lapos listában (a hibás lapok kimaradnak). */
export function rowsOf(feed: NotificationFeed | undefined): NotificationRow[] {
  return (feed?.pages ?? []).flatMap((p) => (p.status === 200 ? p.body.data : []));
}
